import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Returns a callback with a stable identity that always invokes the latest
 * version of `fn` (so it never captures stale props/state). This lets the
 * callback be passed to `React.memo`-wrapped children without forcing them to
 * re-render, while avoiding the stale-closure pitfalls of a plain `useCallback`
 * with an incomplete dependency list.
 */
export function useEventCallback<Args extends unknown[], R>(
    fn: (...args: Args) => R,
): (...args: Args) => R {
    const ref = useRef(fn);

    useLayoutEffect(() => {
        ref.current = fn;
    });

    return useCallback((...args: Args) => ref.current(...args), []);
}
